import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import api from '../lib/api';
import { 
  LayoutDashboard, 
  Package, 
  FileText, 
  Truck, 
  PackageCheck, 
  Users, 
  Settings, 
  LogOut, 
  Menu,
  Database,
  Bell,
  ChevronRight,
  ChevronDown,
  History,
  Shield,
  Lock,
  UserCog,
  Building2
} from 'lucide-react';
import { isAdmin } from '../lib/auth';

const AppShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeCompany, companies } = useCompany();
  const { permissions, hasModulePermission, hasAdvancedPermission, loading: permissionsLoading } = usePermissions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(location.pathname.startsWith('/admin'));
  const [unreadCount, setUnreadCount] = useState(0);

  // Poll for unread notifications every 15 seconds
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await api.get('/notifications/unread-count');
        setUnreadCount(res.data.count);
      } catch {
        // Notification polling failure - non-critical, silently retry on next interval
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  const adminNavItems = [
    { icon: Users, label: 'Users', path: '/admin/users', testId: 'nav-admin-users' },
    { icon: Shield, label: 'Roles', path: '/admin/roles', testId: 'nav-admin-roles' },
    { icon: Lock, label: 'Access Control', path: '/admin/access-control', testId: 'nav-admin-access' },
    { icon: Settings, label: 'Settings', path: '/admin/settings', testId: 'nav-admin-settings' },
  ];

  // All possible nav items with permission requirements
  const allNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', testId: 'nav-dashboard', module: 'dashboard', permission: 'view' },
    { icon: Package, label: 'Grey Orders', path: '/grey-orders', testId: 'nav-grey-orders', module: 'grey_orders', permission: 'view' },
    { icon: FileText, label: 'Grey Bills', path: '/grey-bills', testId: 'nav-grey-bills', module: 'grey_bills', permission: 'view' },
    { icon: Truck, label: 'LR Reports', path: '/lr-reports', testId: 'nav-lr-reports', module: 'lr_reports', permission: 'view' },
    { icon: PackageCheck, label: 'Grey Receipts', path: '/grey-receipts', testId: 'nav-grey-receipts', module: 'grey_receipts', permission: 'view' },
    { icon: Database, label: 'Masters', path: '/masters', testId: 'nav-masters', module: 'masters', permission: 'view' },
    { icon: FileText, label: 'Reports', path: '/reports', testId: 'nav-reports', module: 'reports', permission: 'view' },
    { icon: Bell, label: 'Notifications', path: '/notifications', testId: 'nav-notifications' },
    { icon: History, label: 'Audit Logs', path: '/audit-logs', testId: 'nav-audit-logs' },
  ];

  // Compute nav items based on permissions - recalculates on every render
  // This ensures navigation updates immediately when permissions load
  let navItems = [];
  
  if (permissionsLoading) {
    // While loading, show items without module requirements
    navItems = allNavItems.filter(item => !item.module);
  } else if (permissions?.bypass) {
    // Only admin (system owner) sees all items
    navItems = allNavItems;
  } else {
    // Manager, Staff, and all other roles see items based on DB permissions
    navItems = allNavItems.filter(item => {
      if (!item.module) return true;
      return hasModulePermission(item.module, item.permission);
    });
  }

  // Check if user can access Admin Panel
  const canAccessAdmin = isAdmin() || hasAdvancedPermission('access_admin_panel');

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    const Icon = item.icon;
    const showBadge = item.path === '/notifications' && unreadCount > 0;

    return (
      <Link
        to={item.path}
        data-testid={item.testId}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
          isActive
            ? 'theme-nav-active'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        <Icon className={`h-5 w-5 ${isActive ? 'theme-text' : ''}`} />
        <span>{item.label}</span>
        {showBadge && (
          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {isActive && !showBadge && <ChevronRight className="h-4 w-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-slate-200 shadow-sm">
          {/* Company theme accent bar */}
          <div className="h-1 w-full theme-bg" style={{ backgroundColor: 'var(--c-600)' }}></div>
          <div className="flex items-center gap-2 px-6 py-6 border-b border-slate-200">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <Package className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Jai Matadi</h1>
              <p className="text-xs text-slate-500">Grey Order Mgmt</p>
            </div>
          </div>

          {/* Current Company + Switch (uses the shared Company Selection page) */}
          {companies.length > 0 && (
            <div data-testid="current-company-box" className="px-4 py-3 border-b theme-border" style={{ backgroundColor: 'var(--c-50)', borderBottomColor: 'var(--c-200)' }}>
              <div className="flex items-center gap-2 px-1 mb-2">
                <Building2 className="h-4 w-4 flex-shrink-0 theme-text" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Current Company</p>
                  <p data-testid="active-company-name" className="text-sm font-semibold text-slate-800 truncate">{activeCompany?.name}</p>
                </div>
                <span className="theme-dot ml-auto flex-shrink-0"></span>
              </div>
              {companies.length > 1 && (
                <Button
                  data-testid="switch-company-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/select-company')}
                  className="w-full h-8 text-xs bg-white"
                  style={{ borderColor: 'var(--c-200)' }}
                >
                  <Building2 className="h-3.5 w-3.5 mr-1.5" /> Switch Company
                </Button>
              )}
            </div>
          )}

          <div className="flex-1 flex flex-col gap-1 px-3 py-4 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
            
            {/* Admin Panel Section - Only for Admin users or those with access_admin_panel permission */}
            {canAccessAdmin && (
              <Collapsible open={adminMenuOpen} onOpenChange={setAdminMenuOpen} className="mt-4">
                <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg">
                  <div className="flex items-center gap-3">
                    <UserCog className="h-5 w-5 text-purple-600" />
                    <span>Admin Panel</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform ${adminMenuOpen ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-1 mt-1">
                  {adminNavItems.map((item) => (
                    <NavLink key={item.path} item={item} />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>

          <div className="border-t border-slate-200 p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full theme-avatar flex items-center justify-center">
                <span className="theme-avatar-text font-semibold">{user?.name?.charAt(0)}</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <Badge variant="outline" className="text-xs">
                  {user?.role}
                </Badge>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Jai Matadi</h1>
              {activeCompany && (
                <p data-testid="mobile-active-company" className="text-[10px] font-medium -mt-0.5 theme-text">{activeCompany.code}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="relative" onClick={() => navigate('/notifications')} data-testid="mobile-notification-bell">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button data-testid="mobile-menu-btn" variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-200">
                    <div className="w-12 h-12 rounded-full theme-avatar flex items-center justify-center">
                      <span className="theme-avatar-text font-semibold text-lg">{user?.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-base font-medium text-slate-900">{user?.name}</p>
                      <Badge variant="outline" className="text-xs mt-1">
                        {user?.role}
                      </Badge>
                    </div>
                  </div>

                  {/* Mobile: Current Company + Switch */}
                  {companies.length > 0 && (
                    <div className="mb-4 pb-4 border-b border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 flex-shrink-0 theme-text" />
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Current Company</p>
                          <p className="text-sm font-semibold text-slate-800 truncate">{activeCompany?.name}</p>
                        </div>
                      </div>
                      {companies.length > 1 && (
                        <Button
                          data-testid="switch-company-btn-mobile"
                          variant="outline"
                          size="sm"
                          onClick={() => { setMobileMenuOpen(false); navigate('/select-company'); }}
                          className="w-full h-9 text-sm"
                        >
                          <Building2 className="h-4 w-4 mr-1.5" /> Switch Company
                        </Button>
                      )}
                    </div>
                  )}

                  <div className="flex-1 flex flex-col gap-1 overflow-y-auto">
                    {navItems.map((item) => (
                      <NavLink key={item.path} item={item} mobile />
                    ))}
                    
                    {/* Admin Panel Section - Only for Admin users or those with access_admin_panel permission (Mobile) */}
                    {canAccessAdmin && (
                      <Collapsible open={adminMenuOpen} onOpenChange={setAdminMenuOpen} className="mt-4">
                        <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg">
                          <div className="flex items-center gap-3">
                            <UserCog className="h-5 w-5 text-purple-600" />
                            <span>Admin Panel</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${adminMenuOpen ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pl-4 space-y-1 mt-1">
                          {adminNavItems.map((item) => (
                            <NavLink key={item.path} item={item} mobile />
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>

                  <div className="pt-4 mt-4 border-t border-slate-200">
                    <Button
                      onClick={logout}
                      variant="ghost"
                      className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AppShell;
